import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { Clock, CheckCircle2, XCircle, Ban, ArrowRight } from "lucide-react";

const SellerStatus = () => {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login?next=/seller/status"); return; }
    apiClient.get("/sellers/me").then((r) => setS(r.data)).catch(() => setS(null)).finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink-muted">Loading…</div>;
  if (!s) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h1 className="font-display text-4xl tracking-tighter">{lang === "nl" ? "Geen aanvraag gevonden" : "No application found"}</h1>
        <Link to="/sell/apply" className="btn-primary mt-6">{lang === "nl" ? "Aanvraag starten" : "Start application"} <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  const STATUS = {
    pending:    { Icon: Clock,         tone: "bg-amber-100 text-amber-800",   t_nl: "In behandeling", t_en: "Under review", d_nl: "We beoordelen je gegevens binnen 1-2 werkdagen.", d_en: "We're reviewing your details within 1-2 business days." },
    approved:   { Icon: CheckCircle2,  tone: "bg-emerald-100 text-emerald-800", t_nl: "Goedgekeurd",  t_en: "Approved",     d_nl: "Welkom! Je kunt nu producten toevoegen.",          d_en: "Welcome! You can now add products." },
    rejected:   { Icon: XCircle,       tone: "bg-rose-100 text-rose-800",       t_nl: "Afgewezen",    t_en: "Rejected",     d_nl: "Bekijk de reden hieronder. Je kunt opnieuw aanvragen.", d_en: "See the reason below. You may re-apply." },
    suspended:  { Icon: Ban,           tone: "bg-rose-100 text-rose-800",       t_nl: "Geschorst",    t_en: "Suspended",    d_nl: "Je account is tijdelijk geschorst. Neem contact op met support.", d_en: "Your account is temporarily suspended. Contact support." },
  };
  const meta = STATUS[s.status] || STATUS.pending;
  const Icon = meta.Icon;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16" data-testid="seller-status-page">
      <div className="font-label text-ink-muted">{s.company_name}</div>
      <h1 className="font-display text-5xl tracking-tighter font-light mt-1">{lang === "nl" ? meta.t_nl : meta.t_en}</h1>
      <div className={`inline-flex items-center gap-2 mt-4 px-3 py-1.5 text-xs font-label ${meta.tone}`} data-testid={`seller-status-${s.status}`}>
        <Icon className="w-4 h-4" /> {s.status.toUpperCase()}
      </div>
      <p className="mt-5 text-ink-muted leading-relaxed">{lang === "nl" ? meta.d_nl : meta.d_en}</p>

      {s.rejection_reason && s.status === "rejected" && (
        <div className="mt-6 border-l-2 border-rose-500 pl-4 text-sm">
          <div className="font-label text-rose-600 mb-1">{lang === "nl" ? "Reden" : "Reason"}</div>
          <p>{s.rejection_reason}</p>
        </div>
      )}

      <div className="mt-10 border border-black/10 bg-white p-6 grid md:grid-cols-2 gap-4 text-sm">
        <Row k={lang === "nl" ? "Bedrijf" : "Company"} v={s.company_name} />
        <Row k="KvK" v={s.kvk_number} />
        <Row k="BTW" v={s.btw_number} />
        <Row k={lang === "nl" ? "Ingediend op" : "Submitted"} v={(s.submitted_at || "").slice(0, 10)} />
        <Row k={lang === "nl" ? "Contact" : "Contact"} v={`${s.contact_person} · ${s.contact_email}`} full />
      </div>

      <div className="mt-6 flex gap-3">
        {s.status === "approved" && (
          <Link to="/seller" className="btn-primary" data-testid="goto-seller-dashboard">{lang === "nl" ? "Ga naar dashboard" : "Open dashboard"} <ArrowRight className="w-4 h-4" /></Link>
        )}
        {(s.status === "rejected") && (
          <Link to="/sell/apply" className="btn-primary">{lang === "nl" ? "Opnieuw aanvragen" : "Re-apply"}</Link>
        )}
        <Link to="/" className="btn-secondary">{lang === "nl" ? "Terug naar winkel" : "Back to shop"}</Link>
      </div>
    </div>
  );
};
const Row = ({ k, v, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-xs font-label text-ink-muted">{k}</div>
    <div>{v}</div>
  </div>
);

export default SellerStatus;
